import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Log levels for different types of messages
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

/**
 * Logger configuration
 */
interface LoggerConfig {
  level: LogLevel;
  enableTimestamps: boolean;
  enableColors: boolean;
  enableProduction: boolean;
}

/**
 * Default logger configuration
 */
const defaultConfig: LoggerConfig = {
  level: process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test' ? LogLevel.DEBUG : LogLevel.ERROR,
  enableTimestamps: true,
  enableColors: process.env.NODE_ENV === 'development',
  enableProduction: false, // Only log errors in production by default
};

/**
 * Color codes for different log levels (for development)
 */
const colors = {
  [LogLevel.DEBUG]: '\x1b[36m', // Cyan
  [LogLevel.INFO]: '\x1b[32m',  // Green
  [LogLevel.WARN]: '\x1b[33m',  // Yellow
  [LogLevel.ERROR]: '\x1b[31m', // Red
  reset: '\x1b[0m',
};

/**
 * Log level names
 */
const levelNames = {
  [LogLevel.DEBUG]: 'DEBUG',
  [LogLevel.INFO]: 'INFO',
  [LogLevel.WARN]: 'WARN',
  [LogLevel.ERROR]: 'ERROR',
};

/**
 * Enhanced centralized logger for consistent logging across the application
 */
class Logger {
  private config: LoggerConfig;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  /**
   * Check if a log level should be output
   */
  private shouldLog(level: LogLevel): boolean {
    const isProduction = process.env.NODE_ENV === 'production';
    const isTest = process.env.NODE_ENV === 'test';
    
    // Always allow logging in test environment for testing purposes
    if (isTest) {
      return level >= this.config.level;
    }
    
    if (isProduction && !this.config.enableProduction && level < LogLevel.ERROR) {
      return false;
    }
    
    return level >= this.config.level;
  }

  /**
   * Format a log message with timestamp and colors
   */
  private formatMessage(level: LogLevel, namespace: string, message: string): string {
    const timestamp = this.config.enableTimestamps 
      ? `${new Date().toISOString()} ` 
      : '';
    
    const levelName = levelNames[level];
    const color = this.config.enableColors ? colors[level] : '';
    const reset = this.config.enableColors ? colors.reset : '';
    
    return `${color}${timestamp}[${levelName}] [${namespace}] ${message}${reset}`;
  }

  /**
   * Log a debug message
   */
  debug(namespace: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage(LogLevel.DEBUG, namespace, message), ...args);
    }
  }

  /**
   * Log an info message
   */
  info(namespace: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage(LogLevel.INFO, namespace, message), ...args);
    }
  }

  /**
   * Log a warning message
   */
  warn(namespace: string, message: string, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage(LogLevel.WARN, namespace, message), ...args);
    }
  }

  /**
   * Log an error message
   */
  error(namespace: string, message: string, error?: Error, ...args: unknown[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const errorInfo = error ? `\nError: ${error.message}\nStack: ${error.stack}` : '';
      console.error(this.formatMessage(LogLevel.ERROR, namespace, message + errorInfo), ...args);
    }
  }

  /**
   * Log performance metrics
   */
  performance(namespace: string, operation: string, duration: number): void {
    if (this.shouldLog(LogLevel.INFO)) {
      this.info(namespace, `Performance: ${operation} took ${duration}ms`);
    }
  }

  /**
   * Log user interactions for analytics
   */
  analytics(namespace: string, event: string, data?: Record<string, unknown>): void {
    if (this.shouldLog(LogLevel.INFO)) {
      this.info(namespace, `Analytics: ${event}`, data);
    }
  }
}

// Create a singleton logger instance
const loggerInstance = new Logger();

/**
 * Centralized logger for consistent logging across the application
 * 
 * @deprecated Use the new logger methods instead (logger.debug, logger.info, etc.)
 * @param namespace - The module or component namespace for context
 * @param message - The log message
 * @param args - Additional arguments to log
 */
export function logger(namespace: string, message: string, ...args: unknown[]) {
  loggerInstance.info(namespace, message, ...args);
}

// Export the enhanced logger
export { loggerInstance as log };

/**
 * Create a namespaced logger for a specific module/component
 */
export function createLogger(namespace: string) {
  return {
    debug: (message: string, ...args: unknown[]) => loggerInstance.debug(namespace, message, ...args),
    info: (message: string, ...args: unknown[]) => loggerInstance.info(namespace, message, ...args),
    warn: (message: string, ...args: unknown[]) => loggerInstance.warn(namespace, message, ...args),
    error: (message: string, error?: Error, ...args: unknown[]) => loggerInstance.error(namespace, message, error, ...args),
    performance: (operation: string, duration: number) => loggerInstance.performance(namespace, operation, duration),
    analytics: (event: string, data?: Record<string, unknown>) => loggerInstance.analytics(namespace, event, data),
  };
}
